import React, { useEffect, useRef, useState } from "react";
import { AppShell } from "../components/cod/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { refApi } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { CODLogoSVG } from "../components/cod/Logo";
import { MessageSquare, Send, X } from "lucide-react";

export default function PartnerReferrals() {
  const [refs, setRefs] = useState([]);
  const [openThread, setOpenThread] = useState(null); // referral object

  const reload = () => refApi.mine().then(setRefs).catch(()=>{});
  useEffect(()=>{ reload(); }, []);

  const setOutcome = async (id, outcome) => {
    try {
      await refApi.outcome(id, { outcome, notes: "" });
      toast.success("Outcome recorded", { icon: <CODLogoSVG size={22}/> });
      reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not record outcome"); }
  };

  return (
    <AppShell>
      <div className="space-y-6" data-testid="partner-referrals-page">
        <div>
          <h1 className="font-serif text-3xl text-slate-900">Referrals from COD</h1>
          <p className="text-slate-600 mt-1">Participants COD has referred to you. Reply to the assigned Case Manager directly on each referral.</p>
        </div>
        <div className="space-y-3">
          {refs.map(r => (
            <Card key={r.id} data-testid={`ref-${r.id}`}>
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:justify-between gap-2">
                  <CardTitle className="font-serif text-lg">{r.participant?.full_name || "Participant"}</CardTitle>
                  <div className="flex gap-2 flex-wrap">
                    <Badge variant={r.priority === "P1" ? "destructive" : "outline"}>{r.priority}</Badge>
                    <Badge variant="secondary">{r.status}</Badge>
                    {r.unread_messages > 0 && (
                      <Badge className="text-white" style={{background:"var(--cod-red)"}} data-testid={`unread-${r.id}`}>
                        {r.unread_messages} new
                      </Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-slate-700">{r.summary}</p>
                {r.outcome && <p className="text-sm mt-2 text-slate-600">Outcome: <Badge>{r.outcome}</Badge></p>}
                <div className="flex flex-wrap gap-2 mt-4">
                  {!r.outcome && (
                    <>
                      <Button size="sm" onClick={()=>setOutcome(r.id, "accepted")} data-testid={`accept-${r.id}`}>Accept</Button>
                      <Button size="sm" variant="outline" onClick={()=>setOutcome(r.id, "completed")} data-testid={`complete-${r.id}`}>Mark complete</Button>
                      <Button size="sm" variant="ghost" onClick={()=>setOutcome(r.id, "declined")} data-testid={`decline-${r.id}`}>Decline</Button>
                    </>
                  )}
                  <Button size="sm" variant="outline" onClick={()=>setOpenThread(r)} data-testid={`msg-${r.id}`}>
                    <MessageSquare size={14} className="mr-1"/> Message Case Manager
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
          {refs.length === 0 && <p className="text-sm text-slate-500">No referrals yet.</p>}
        </div>
      </div>

      {openThread && (
        <ReferralThreadDrawer
          referral={openThread}
          onClose={() => { setOpenThread(null); reload(); }}
        />
      )}
    </AppShell>
  );
}

export function ReferralThreadDrawer({ referral, onClose }) {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [assignedStaffName, setAssignedStaffName] = useState("");
  const scrollRef = useRef(null);

  const reload = async () => {
    try {
      const [t, r] = await Promise.all([
        refApi.thread(referral.id),
        refApi.get(referral.id),
      ]);
      setMsgs(t);
      setAssignedStaffName(r?.assigned_staff?.full_name || "Case Manager");
    } catch (err) { toast.error("Could not load thread"); }
  };
  useEffect(() => { reload(); }, [referral.id]);
  useEffect(() => {
    // Auto-scroll to newest
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs]);

  const send = async (e) => {
    e.preventDefault();
    const body = text.trim();
    if (!body) return;
    setBusy(true);
    try {
      await refApi.post(referral.id, body);
      setText("");
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-slate-900/40 z-40"
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className="fixed right-0 top-0 h-full w-full sm:w-[440px] bg-white shadow-2xl z-50 flex flex-col"
        role="dialog"
        aria-label="Referral message thread"
        data-testid="referral-thread"
      >
        <header className="p-4 border-b border-slate-200 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="cod-eyebrow">Referral thread</p>
            <p className="font-serif text-lg text-slate-900 truncate mt-1">{referral.participant?.full_name || "Participant"}</p>
            <p className="text-xs text-slate-500 truncate">With {assignedStaffName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-md hover:bg-slate-100 text-slate-500"
            aria-label="Close thread"
            data-testid="thread-close"
          >
            <X size={18}/>
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50" ref={scrollRef} data-testid="thread-messages">
          {msgs.length === 0 && (
            <p className="text-center text-sm text-slate-500 mt-8">
              No messages yet. Start the conversation below.
            </p>
          )}
          {msgs.map(m => {
            const mine = m.from_user_id === user?.id;
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-lg px-3 py-2 shadow-sm ${mine ? "text-white" : "bg-white text-slate-800 border border-slate-200"}`}
                     style={mine ? { background: "var(--cod-navy)" } : {}}>
                  <p className="text-sm whitespace-pre-wrap break-words">{m.body}</p>
                  <p className={`text-[10px] mt-1 ${mine ? "text-slate-200" : "text-slate-400"}`}>
                    {new Date(m.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        <form onSubmit={send} className="p-3 border-t border-slate-200 bg-white">
          <div className="flex gap-2 items-end">
            <Textarea
              value={text}
              onChange={e => setText(e.target.value)}
              placeholder={`Message ${assignedStaffName}...`}
              rows={2}
              className="flex-1 resize-none"
              data-testid="thread-input"
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(e);
              }}
            />
            <Button type="submit" disabled={busy || !text.trim()}
              style={{ background: "var(--cod-red)" }}
              data-testid="thread-send">
              <Send size={14} className="mr-1"/> Send
            </Button>
          </div>
          <p className="text-[10px] text-slate-400 mt-1">⌘/Ctrl + Enter to send</p>
        </form>
      </aside>
    </>
  );
}
